import { FloatingContext, Tooltip } from "@/components/TooltipV2";
import { ChalkEmployeeGuard } from "@/components/admin/ChalkEmployeeGuard";
import { SearchButton } from "@/components/command-palette/SearchButton";
import { Toggle } from "@/components/form/Toggle";
import { IconLinkButtonV2 } from "@/components/interactive/ButtonV2";
import { ThemeToggle } from "@/components/interactive/ThemeToggle";
import { NavLink } from "@/components/interactive/NavLink";
import { useGlobalAppState } from "@/utils/app-state";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { useSession } from "next-auth/react";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import React, { ReactNode, useRef } from "react";
import { Routes } from "utils/routes";
import { LogoSquare } from "../branding/logos";
import { EngineImageOverrideWarning } from "./EngineImageOverrideWarning";
import { ProfileDropdown } from "./ProfileDropdown";
import { ProjectDropdown } from "./ProjectDropdown";
import { SupportDropdown } from "./SupportDropdown";
import { FlagV2Guard } from "@/components/flags/FlagV2Guard";

export interface NavBarProps {
  loggedIn?: boolean;
  absoluteLabel?: ReactNode;
}

export const NavBar: React.FC<NavBarProps> = ({ loggedIn, absoluteLabel }) => {
  const session = useSession();

  const { projectId, environmentId } = useRouteInfo();

  const {
    state: { adminMode },
    updateState,
  } = useGlobalAppState();

  const navBarRef = useRef<HTMLDivElement>(null);

  return (
    <FloatingContext.Provider value={navBarRef.current}>
      <div className={"relative"} ref={navBarRef}>
        <nav className="bg-white border-b">
          <div className="px-5">
            <div className="flex justify-start items-center h-16 space-x-1">
              {!loggedIn && (
                <div className="flex items-center">
                  <ChalkLink
                    href={loggedIn ? Routes.projects : Routes.home}
                    legacyBehavior
                    unstyled
                  >
                    <button>
                      {/* <LogoType className="hidden sm:inline-block" /> */}
                      <LogoSquare size={"sm"} />
                    </button>
                  </ChalkLink>
                </div>
              )}
              {loggedIn && (
                <>
                  <div className="flex items-center flex-1 gap-1">
                    <ChalkLink
                      href={loggedIn ? Routes.projects : Routes.home}
                      legacyBehavior
                      unstyled
                    >
                      <button>
                        {/* <LogoType className="hidden sm:inline-block" /> */}
                        <LogoSquare size={"sm"} />
                      </button>
                    </ChalkLink>
                    <ProjectDropdown />
                  </div>

                  <SearchButton />

                  {/* Desktop */}
                  <div className="xs:flex ml-6 gap-2 items-center flex-1 justify-end">
                    <ChalkEmployeeGuard hideTooltip ignoreAdminMode>
                      <Tooltip content={adminMode ? "Admin mode on" : "Admin mode off"}>
                        <div className={"mx-1"}>
                          <Toggle
                            size={"sm"}
                            checked={adminMode}
                            onChange={(v) => updateState({ adminMode: v })}
                          />
                        </div>
                      </Tooltip>
                    </ChalkEmployeeGuard>
                    <ThemeToggle />
                    <ChalkEmployeeGuard ignoreAdminMode>
                      {projectId && environmentId ? (
                        <IconLinkButtonV2
                          aria-label={"Open admin"}
                          icon="terminal"
                          iconSize="lg"
                          href={Routes.project.environment.admin(projectId, environmentId)}
                          size="sm"
                          variant="secondary"
                        />
                      ) : null}
                    </ChalkEmployeeGuard>
                    {projectId && environmentId ? (
                      <IconLinkButtonV2
                        aria-label={"Open settings"}
                        icon="settings"
                        iconSize="lg"
                        href={Routes.project.environment.settings(projectId, environmentId)}
                        size="sm"
                        variant="secondary"
                      />
                    ) : null}
                    <IconLinkButtonV2
                      aria-label={"Open documentation"}
                      icon="docs"
                      iconSize="lg"
                      href="https://docs.chalk.ai"
                      size="sm"
                      variant="secondary"
                    />
                    <SupportDropdown />

                    {session.status === "unauthenticated" ? (
                      <NavLink href="/api/auth/signin">Sign In</NavLink>
                    ) : (
                      <ProfileDropdown />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </nav>
        {absoluteLabel != null && <div className={"absolute w-full"}>{absoluteLabel}</div>}
      </div>
    </FloatingContext.Provider>
  );
};

export const SimpleNavBar: React.FC = () => {
  return (
    <div className={"relative"}>
      <nav className="bg-white border-b">
        <div className="px-5">
          <div className="flex justify-start items-center h-16 space-x-1">
            <div className="flex items-center">
              <ChalkLink href={Routes.projects} legacyBehavior unstyled>
                <button>
                  <LogoSquare size={"sm"} />
                </button>
              </ChalkLink>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};
